// dependencies
import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../../lib/utils";

// components
import { Tooltip } from "../Tooltip";

export interface BadgeProps {
	/**
	 * The count to display in the badge. If the number is greater than 999, it will display "+999".
	 */
	count: number;
	/**
	 * The max count to display when the count exceeds the overflowCount. Will be preceded by a "+" sign. Cannot be greater than 999.
	 */
	overflowCount?: number;
	/**
	 * If true, the badge will have a border
	 */
	border?: boolean;
	/**
	 * The tooltip to display when hovering over the badge
	 */
	tooltip?: string | React.ReactNode;
	/**
	 * A class name to apply to the badge
	 */
	dangerouslySetClassName?: string;
}

const badgeVariants = cva("text- flex size-6 items-center justify-center rounded-full bg-neutral-2 text-neutral-9", {
	variants: {
		border: {
			true: "border border-solid border-neutral-0",
		},
	},
	defaultVariants: {
		border: false,
	},
});

const getCountDisplay = (count: number, overflowCount: number | undefined) => {
	// display count if no overflowCount specified and count is less than 1000
	if (!overflowCount && count < 1000) {
		return `${count}`;
	}
	if (overflowCount && overflowCount < 1000) {
		return count > overflowCount ? `+${overflowCount}` : `${count}`;
	}
	// display +999 if count is greater than 999
	return `+999`;
};

// get the typography based on number of characters
const getTypography = (countDisplay: string) => {
	// 3 characters of less
	if (countDisplay.length <= 3) {
		return "text-caption";
	}
	// 4 characters
	if (countDisplay.length === 4) {
		return "text-extra-small";
	}
};

/**
 * The badge component is used to display the count of items. To use it, include it as a child of the component you want to attach it to.
 */
const Badge = (props: BadgeProps) => {
	const { count, overflowCount, border, dangerouslySetClassName, tooltip } = props;
	const countDisplay = getCountDisplay(count, overflowCount);
	const typography = getTypography(countDisplay);

	const badgeContent = (
		<div className={cn(badgeVariants({ border }), `${dangerouslySetClassName} ${typography}`)}>{countDisplay}</div>
	);

	return (
		<div className="absolute -right-4 -top-3">
			{tooltip ? <Tooltip content={tooltip}>{badgeContent}</Tooltip> : badgeContent}
		</div>
	);
};

export { Badge };
